import { useMemo, type ReactNode } from "react";
import { OWL_TIER_META, type OwlHourSlip, type OwlTier } from "../../owl/useOwlHourSlips.ts";
import { OWL_STATE_COPY, isLockCtaActive, type OwlScreenState } from "../../owl/owlScreenState.ts";
import { useSlipRouting } from "../../routing/useSlipRouting.ts";
import { RouteBadge } from "../../components/RouteBadge.tsx";
import { DNABadge } from "../../components/DNABadge.tsx";
import { OwlLegRow } from "./OwlLegRow.tsx";
export interface OwlSignalCardProps {
  slip: OwlHourSlip | undefined;
  tierKey: string;
  lockedStake?: number | null;
  onLockClick?: () => void;
  stakeAction?: ReactNode;
  screenState?: OwlScreenState;
  allSlips?: readonly OwlHourSlip[];
  onTierDegrade?: (tier: OwlTier) => void;
}
export function OwlSignalCard({
  slip,
  tierKey,
  lockedStake,
  onLockClick,
  stakeAction,
  screenState,
  allSlips,
  onTierDegrade,
}: OwlSignalCardProps): JSX.Element {
  const meta = OWL_TIER_META[tierKey as OwlTier],
    legs = useMemo(() => slip?.legs ?? [], [slip]);
  const routingLegs = useMemo(
    () => legs.map((l) => ({ fixture_id: l.fixture_id, market: l.market, selection: l.selection })),
    [legs],
  );
  const routing = useSlipRouting(routingLegs);
  if (!slip) {
    const available = (allSlips ?? []).find((s) => s.tier !== tierKey && s.legs.length > 0);
    const copy =
      screenState === "PRE_SCAN"
        ? OWL_STATE_COPY.PRE_SCAN
        : screenState === "SCANNING"
          ? OWL_STATE_COPY.SCANNING
          : "No published board is available for this tier.";
    return (
      <section className="owl-slip owl-board-empty" data-testid="owl-signal-card-pre-publish">
        <span className="owl-eyebrow">{meta.name}</span>
        <h2>No published selections yet</h2>
        <p data-testid="owl-state-copy">{copy}</p>
        <p>
          Refreshes run at 12:00 and 17:00 Accra. Each board appears when its publication is
          available.
        </p>
        {available && onTierDegrade ? (
          <button type="button" className="btn" onClick={() => onTierDegrade(available.tier)}>
            View {OWL_TIER_META[available.tier].name} ↗
          </button>
        ) : null}
      </section>
    );
  }
  const priceAvailable =
    Number.isFinite(slip.combined_odds) &&
    Number(slip.combined_odds) > 1 &&
    legs.length > 0 &&
    legs.every((l) => Number.isFinite(l.odds) && l.odds > 1);
  const stake = lockedStake ?? meta.defaultStake,
    isLocked = lockedStake !== null && lockedStake !== undefined && lockedStake > 0;
  const canLock =
    priceAvailable && (screenState ? isLockCtaActive(screenState) : slip.status === "published");
  return (
    <section className="owl-slip owl-publication-board" aria-label={`${meta.name} selection board`}>
      <header className="owl-publication-header">
        <div>
          <span className="owl-eyebrow">PUBLISHED BOARD</span>
          <h2>{meta.name}</h2>
          <p>
            {slip.slip_date} · {legs.length} selections · {slip.leagues_covered.length} competitions
          </p>
        </div>
        <div className="owl-board-total">
          <span>Recorded combined odds</span>
          <strong>
            {priceAvailable
              ? Number(slip.combined_odds).toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "Unavailable"}
          </strong>
          <span className="owl-state-pill">
            {slip.result
              ? slip.result === "win"
                ? "Won"
                : slip.result === "loss"
                  ? "Lost"
                  : slip.result === "void"
                    ? "Void"
                    : "Partial"
              : slip.status === "settled"
                ? "Awaiting verified result"
                : slip.meta.reduced_board
                  ? "Reduced board"
                  : "Published"}
          </span>
        </div>
      </header>
      <div className="owl-board-note">
        Original selections and prices stay recorded. Results update when verified.
      </div>
      <div className="owl-board-columns" aria-hidden="true">
        <span>#</span>
        <span>Fixture / competition</span>
        <span>Market / selection</span>
        <span>Odds</span>
        <span>Status</span>
      </div>
      <div className="owl-legs">
        {legs.map((leg, i) => (
          <OwlLegRow
            key={`${leg.fixture_id}:${leg.market}:${leg.selection}:${i}`}
            leg={leg}
            index={i}
            isLast={i === legs.length - 1}
            extras={
              <>
                <RouteBadge
                  route={routing.lookup(leg.fixture_id, leg.market, leg.selection)}
                  placeholder={routing.loading}
                  compact
                />
                <DNABadge fixtureId={leg.fixture_id} market={leg.market} compact />
              </>
            }
          />
        ))}
      </div>
      <footer className="owl-publication-footer">
        <div>
          <strong>
            {isLocked ? "Saved stake" : "Your stake"} · GH₵{stake}
          </strong>
          <span>
            {priceAvailable
              ? `Potential return GH₵${Math.round(stake * Number(slip.combined_odds)).toLocaleString()} if every selection wins.`
              : "Current pricing is required before saving a stake."}
          </span>
        </div>
        {stakeAction ?? (
          <button
            type="button"
            className="btn primary"
            disabled={isLocked ? slip.status !== "published" : !canLock}
            onClick={onLockClick}
          >
            {isLocked ? "Change stake" : "Review stake"}
            <span aria-hidden="true"> ↗</span>
          </button>
        )}
      </footer>
    </section>
  );
}
